import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'

// Updated types to match actual API response
export interface PrescriptionRenewal {
  patient_medication_id: string
  name: string
  due_date: string | null
  on_next_step?: boolean
}

export interface PrescriptionRenewalData {
  medications: PrescriptionRenewal[]
  number_of_prescriptions: number
  visit_options: string[]
}

export interface PrescriptionRenewalDestinationResponse {
  next_step_id: string | null
  has_gm_prescribed_medications?: boolean
  fallback_sku_id: number
}

// Normalized pricing data from transformResponse
export interface TransformedSkuOptionsData {
  pricing: {
    videoOption?: {
      insurancePrice?: number
      cashPrice?: number
      mdsavePrice?: number
    }
    chatOption?: {
      insurancePrice?: number
      cashPrice?: number
      mdsavePrice?: number
    }
    hasVideo: boolean
    hasChat: boolean
    hasInsurance: boolean
    preferredPrice?: number
    preferredText?: string
    secondaryPrice?: number
    secondaryText?: string
  }
  authenticated: boolean
  raw_options: any[] // Keep original for debugging
}

// Prescription Renewal API slice
export const prescriptionRenewalApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPrescriptionRenewalDestination: builder.query<PrescriptionRenewalDestinationResponse, void>({
      query: () => apiEndpoints.care.getPrescriptionRenewalDestination,
      providesTags: ['Prescription'],
    }),
    getPrescriptionRenewal: builder.query<PrescriptionRenewalData, string>({
      query: (nextStepId  ) => apiEndpoints.care.getNextStepPrescriptionRenewal(nextStepId),
      providesTags: (result, error, nextStepId) => [
        { type: 'Prescription', id: nextStepId },
        'Prescription'
      ],
    }),

    // New endpoint for SKU options with next_step_id - with normalized pricing
    getSkuOptionsWithNextStep: builder.query<TransformedSkuOptionsData, { skuId: string; nextStepId?: string }>({
      query: ({ skuId, nextStepId }) => {
        return nextStepId
          ? apiEndpoints.skus.getOptionsWithNextStep(skuId, nextStepId)
          : apiEndpoints.skus.getOptions(skuId)
      },
      transformResponse: (response: any) => {
        console.log('🔵 SKU Options with Next Step Response:', response)
        
        // Extract pricing data from complex nested structure
        const videoOption = response.options?.find((opt: any) => opt.type === 'video')
        const chatOption = response.options?.find((opt: any) => opt.type === 'chat')
        
        const videoPricing = videoOption ? {
          insurancePrice: videoOption.options?.find((opt: any) => opt.type === 'insurance')?.price?.amount,
          cashPrice: videoOption.options?.find((opt: any) => opt.type === 'cash')?.price?.amount,
          mdsavePrice: videoOption.options?.find((opt: any) => opt.type === 'mdsave')?.price?.amount,
        } : undefined
        
        const chatPricing = chatOption ? {
          insurancePrice: chatOption.options?.find((opt: any) => opt.type === 'insurance')?.price?.amount,
          cashPrice: chatOption.options?.find((opt: any) => opt.type === 'cash')?.price?.amount,
          mdsavePrice: chatOption.options?.find((opt: any) => opt.type === 'mdsave')?.price?.amount,
        } : undefined
        
        // Determine available options
        const hasVideo = !!videoOption
        const hasChat = !!chatOption
        
        // Get all available prices
        const insurancePrice = videoPricing?.insurancePrice || chatPricing?.insurancePrice
        const nonInsurancePrice = videoPricing?.cashPrice || videoPricing?.mdsavePrice || 
                                  chatPricing?.cashPrice || chatPricing?.mdsavePrice
        const hasInsurance = !!insurancePrice
        
        let preferredPrice: number | undefined
        let preferredText: string | undefined
        let secondaryPrice: number | undefined
        let secondaryText: string | undefined
        
        if (insurancePrice && nonInsurancePrice) {
          const insuranceText = response.authenticated ? 'with your insurance' : 'typical copay'
          const nonInsuranceText = (videoPricing?.cashPrice || chatPricing?.cashPrice) ? 'w/o insurance' : 'without insurance'
          
          if (insurancePrice <= nonInsurancePrice) {
            preferredPrice = insurancePrice
            preferredText = insuranceText
            secondaryPrice = nonInsurancePrice
            secondaryText = nonInsuranceText
          } else {
            preferredPrice = nonInsurancePrice
            preferredText = nonInsuranceText
            secondaryPrice = insurancePrice
            secondaryText = insuranceText
          }
        } else if (insurancePrice) {
          preferredPrice = insurancePrice
          preferredText = response.authenticated ? 'with your insurance' : 'typical copay'
        } else if (nonInsurancePrice) {
          preferredPrice = nonInsurancePrice
          preferredText = (videoPricing?.cashPrice || chatPricing?.cashPrice) ? 'w/o insurance' : 'without insurance'
        }
        
        return {
          pricing: {
            videoOption: videoPricing,
            chatOption: chatPricing,
            hasVideo,
            hasChat,
            hasInsurance,
            preferredPrice,
            preferredText,
            secondaryPrice,
            secondaryText,
          },
          authenticated: response.authenticated,
          raw_options: response.options || []
        }
      },
      providesTags: ['Prescription'],
    }),

    declineNextStep: builder.mutation<void, { id: string; cancellation_reason: string; cancellation_reason_other?: string }>({
      query: ({ id, cancellation_reason, cancellation_reason_other }) => ({
        url: apiEndpoints.care.declineNextStep(id),
        method: 'POST',
        body: {
          cancellation_reason,
          ...(cancellation_reason_other && { cancellation_reason_other }),
        },
      }),
      invalidatesTags: ['Prescription', 'Dashboard'],
    }),

    completedElsewhereNextStep: builder.mutation<void, string>({
      query: (id) => ({
        url: apiEndpoints.care.completedElsewhereNextStep(id),
        method: 'POST',
      }),
      invalidatesTags: ['Prescription', 'Dashboard'],
    }),
  }),
})

export const {
  useGetPrescriptionRenewalDestinationQuery,
  useGetPrescriptionRenewalQuery,
  useGetSkuOptionsWithNextStepQuery,
  useDeclineNextStepMutation,
  useCompletedElsewhereNextStepMutation,
} = prescriptionRenewalApi 